import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { QuickViewService } from '../../services/quick-view.service';

/**
 * <app-quick-view-popover>
 *
 * Popover global — renderiza el template + data del `QuickViewService.state()`.
 * Se monta UNA sola vez en app.component (root). El service tiene un signal de
 * estado; cuando alguien lo llena, este componente renderiza el popover en la
 * posición dada.
 *
 * Position: el x/y del state son top-left del popover. El componente ajusta si
 * se sale del viewport (clamp a viewport - 320px de ancho default).
 *
 * Cierre:
 *   - Caller llama `service.close()`.
 *   - Click en cualquier lado fuera del popover.
 *   - Scroll del documento (cancela el quick view si el user empieza a navegar).
 */
@Component({
  selector: 'app-quick-view-popover',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="qvp-root"
         *ngIf="visibleState() as s"
         [style.left.px]="clampedX(s.x)"
         [style.top.px]="clampedY(s.y)"
         (click)="$event.stopPropagation()"
         @fadeScale
         role="tooltip">
      <ng-container *ngTemplateOutlet="s.template; context: { $implicit: s.data, data: s.data }"></ng-container>
    </div>
  `,
  styles: [`
    :host { display: contents; }
    .qvp-root {
      position: fixed;
      z-index: 9990;
      width: 320px;
      max-width: calc(100vw - 24px);
      background: var(--clay-surface, var(--base-bg, #fff));
      border: 1px solid color-mix(in srgb, var(--base-text-color, #2b2545) 10%, transparent);
      border-radius: 14px;
      box-shadow:
        var(--clay-top-highlight, inset 0 1px 0 rgba(255, 255, 255, 0.55)),
        0 8px 24px rgba(0, 0, 0, 0.16),
        0 16px 40px color-mix(in srgb, var(--crm-accent-1, #1890ff) 12%, transparent);
      padding: 14px 16px;
      pointer-events: auto;
    }
  `],
  animations: [
    trigger('fadeScale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(6px) scale(0.97)' }),
        animate('180ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('120ms ease-in', style({ opacity: 0, transform: 'scale(0.98)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickViewPopoverComponent {
  private service = inject(QuickViewService);

  visibleState = this.service.state;

  clampedX(x: number): number {
    if (typeof window === 'undefined') return x;
    return Math.max(12, Math.min(x, window.innerWidth - 332));
  }
  clampedY(y: number): number {
    if (typeof window === 'undefined') return y;
    return Math.max(12, Math.min(y, window.innerHeight - 200));
  }
}
